import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE = "https://syndafam.com";
const DEFAULT_OG_IMAGE = `${SITE}/og-image.jpg?v=2`;

/**
 * Reusable per-page SEO tags: title, description, canonical, OpenGraph, Twitter card,
 * and optional JSON-LD blobs (accepts a single object or an array of objects).
 * Also renders a BreadcrumbList automatically for non-home routes when `breadcrumbs`
 * are omitted, using the pathname.
 */
export default function Seo({
  title,
  description,
  path,               // optional override; defaults to current location
  image = DEFAULT_OG_IMAGE,
  imageAlt = "SyndaFam Music Group",
  type = "website",
  jsonLd,             // object | object[]
  breadcrumbs,        // [{ name, url }] — overrides auto breadcrumb
  noindex = false,
}) {
  const location = useLocation();
  const pathname = path || location.pathname || "/";
  const canonical = `${SITE}${pathname === "/" ? "" : pathname.replace(/\/+$/, "")}`;

  // Auto-generate a BreadcrumbList when caller didn't pass one and we're not on Home
  let autoCrumbs = null;
  if (!breadcrumbs && pathname !== "/") {
    const parts = pathname.split("/").filter(Boolean);
    const items = [{ name: "Home", url: SITE }];
    let acc = "";
    for (const p of parts) {
      acc += `/${p}`;
      const nice = p.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      items.push({ name: nice, url: `${SITE}${acc}` });
    }
    autoCrumbs = items;
  }
  const crumbList = breadcrumbs || autoCrumbs;
  const crumbLd = crumbList
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbList.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c.name,
          item: c.url,
        })),
      }
    : null;

  const ldBlobs = []
    .concat(jsonLd || [])
    .concat(crumbLd ? [crumbLd] : []);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="SyndaFam" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@syndafam" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {ldBlobs.map((blob, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(blob)}
        </script>
      ))}
    </Helmet>
  );
}
